/**
 * The controls for a simulated firm.
 *
 * A development surface, and the only screen in this console that exists to make things
 * happen rather than to show what has happened: cars that drive themselves, orders that
 * arrive on their own, a desk that rings. It appears on the two simulated firms and nowhere
 * else, because the endpoints behind it are mapped only on a development host and refused
 * for any firm the platform has not marked — which is how `SettingsArea` decides whether to
 * offer it at all.
 *
 * Polling rather than the dispatcher hub, deliberately. Everything here changes every second
 * and matters to one person looking at one screen; adding events to a hub the whole office
 * shares, for a thing that does not exist in production, would be a permanent cost for a
 * temporary reader.
 *
 * One request saves the settings, so the page carries the `SaveBar` rather than a Save button
 * in the row of actions: the draft is edited freely and the bar appears the moment it differs
 * from what the last poll brought back. The actions beside it — five orders, a ring, a reset —
 * are not settings and fire immediately, as they always have.
 */

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import {
  callerKinds,
  simulationApi,
  type CallerKind,
  type SimulationSettings,
  type SimulationState,
} from '../api';
import { describe } from '../errors';
import {
  Banner,
  Button,
  EmptyState,
  Measured,
  PageHeader,
  SaveBar,
  Section,
  Select,
  SettingRow,
  Sub,
  Switch,
  Table,
  Td,
  Th,
  Tr,
} from '../../ui';

/** How often the screen asks again while it is open. */
const RefreshMs = 5000;

export function SimulationPage() {
  const { t } = useTranslation();

  const [state, setState] = useState<SimulationState | null>(null);
  const [draft, setDraft] = useState<SimulationSettings | null>(null);
  const [kind, setKind] = useState<CallerKind>('known');
  const [busy, setBusy] = useState(false);
  const [problem, setProblem] = useState<string | null>(null);

  // While somebody is dragging a slider, the poll must not pull the value back from under
  // them. It keeps refreshing the counters and the cars either way.
  const editing = useRef(false);

  const load = useCallback(async () => {
    try {
      const next = await simulationApi.state();

      setState(next);

      if (!editing.current) setDraft(next.settings);
    } catch (error) {
      setProblem(describe(error, t, 'settings.couldNotLoad'));
    }
  }, [t]);

  useEffect(() => {
    // eslint-disable-next-line react/set-state-in-effect
    void load();

    const timer = setInterval(() => void load(), RefreshMs);

    return () => clearInterval(timer);
  }, [load]);

  /**
   * How many settings differ from what the last poll brought back — the number the save bar
   * counts. Read against the polled state rather than a frozen copy, because a save landing
   * anywhere else would otherwise leave this page claiming changes nobody made.
   */
  const changes = useMemo(() => {
    if (state === null || draft === null) return 0;

    const loaded = state.settings;

    return (Object.keys(loaded) as (keyof SimulationSettings)[]).filter(
      (key) => loaded[key] !== draft[key],
    ).length;
  }, [state, draft]);

  const header = (
    <PageHeader
      title={t('nav.settings.simulation')}
      description={t('nav.settingsAbout.simulation')}
    />
  );

  if (state === null || draft === null) {
    return (
      <>
        {header}
        {problem === null ? (
          <EmptyState>{t('settings.loading')}</EmptyState>
        ) : (
          <Banner tone="danger">{problem}</Banner>
        )}
      </>
    );
  }

  if (!state.available) {
    return (
      <>
        {header}
        <EmptyState>{t('simulation.notRunning')}</EmptyState>
      </>
    );
  }

  const change = <K extends keyof SimulationSettings>(key: K, value: SimulationSettings[K]) => {
    editing.current = true;
    setDraft({ ...draft, [key]: value });
  };

  async function act(work: () => Promise<unknown>) {
    setBusy(true);
    setProblem(null);

    try {
      await work();
      editing.current = false;
      await load();
    } catch (error) {
      setProblem(describe(error, t, 'simulation.couldNotChange'));
    } finally {
      setBusy(false);
    }
  }

  const save = () => act(() => simulationApi.save(draft));

  const onShift = state.bots.filter((bot) => bot.phase !== 'SignedOff');

  return (
    <>
      {header}

      <Section
        title={t('simulation.title', { city: state.city })}
        description={t('simulation.hint')}
      >
        <div className="flex flex-wrap items-center gap-2 px-5 py-3.5">
          <Button disabled={busy} onClick={() => void act(() => simulationApi.createOrders(5))}>
            {t('simulation.createOrders')}
          </Button>

          <Select
            className="w-[190px]"
            value={kind}
            disabled={busy}
            aria-label={t('simulation.callerLabel')}
            onChange={(event) => setKind(event.target.value as CallerKind)}
          >
            {callerKinds.map((name) => (
              <option key={name} value={name}>
                {t(`simulation.caller.${name}`)}
              </option>
            ))}
          </Select>

          <Button disabled={busy} onClick={() => void act(() => simulationApi.ring(kind, true))}>
            {t('simulation.ring')}
          </Button>

          <Button
            variant="danger"
            disabled={busy}
            onClick={() => void act(() => simulationApi.reset())}
          >
            {t('simulation.reset')}
          </Button>
        </div>

        {/* Said on the screen rather than only in the documentation. Somebody who rings the
            desk and hears nothing should not spend ten minutes on their headset. */}
        <p className="text-secondary text-ink-3 px-5 pb-3.5 m-0 max-w-[72ch] leading-[1.4]">
          {t('simulation.noAudio')}
        </p>

        {problem !== null && (
          <Banner tone="danger" footer>
            {problem}
          </Banner>
        )}
      </Section>

      <Section title={t('simulation.drivers')}>
        <SettingRow label={t('simulation.running')} help={t('simulation.runningHint')}>
          <Switch
            checked={draft.running}
            disabled={busy}
            label={t('simulation.running')}
            onChange={(next) => change('running', next)}
          />
        </SettingRow>

        <SettingRow label={t('simulation.activeDrivers')} help={t('simulation.activeDriversHint')}>
          <Measured
            unit={t('settings.unitDrivers')}
            type="number"
            min={0}
            max={50}
            disabled={busy}
            aria-label={t('simulation.activeDrivers')}
            value={draft.activeDrivers}
            onChange={(event) => change('activeDrivers', Number(event.target.value))}
          />
        </SettingRow>

        <SettingRow
          label={t('simulation.outsideCity')}
          help={t('simulation.outsideCityHint', { city: state.city })}
        >
          <Measured
            unit={t('settings.unitDrivers')}
            type="number"
            min={0}
            max={10}
            disabled={busy}
            aria-label={t('simulation.outsideCity')}
            value={draft.driversOutsideCity}
            onChange={(event) => change('driversOutsideCity', Number(event.target.value))}
          />
        </SettingRow>

        <SettingRow
          label={t('simulation.acceptProbability')}
          help={t('simulation.acceptProbabilityHint')}
          last
        >
          <Measured
            unit=""
            type="number"
            min={0}
            max={1}
            step={0.05}
            disabled={busy}
            aria-label={t('simulation.acceptProbability')}
            value={draft.acceptProbability}
            onChange={(event) => change('acceptProbability', Number(event.target.value))}
          />
        </SettingRow>
      </Section>

      <Section title={t('simulation.work')}>
        <SettingRow label={t('simulation.ordersPerHour')} help={t('simulation.ordersPerHourHint')}>
          <Measured
            unit=""
            type="number"
            min={0}
            max={240}
            disabled={busy}
            aria-label={t('simulation.ordersPerHour')}
            value={draft.ordersPerHour}
            onChange={(event) => change('ordersPerHour', Number(event.target.value))}
          />
        </SettingRow>

        <SettingRow
          label={t('simulation.callsPerHour')}
          help={t('simulation.callsPerHourHint')}
          last
        >
          <Measured
            unit=""
            type="number"
            min={0}
            max={120}
            disabled={busy}
            aria-label={t('simulation.callsPerHour')}
            value={draft.callsPerHour}
            onChange={(event) => change('callsPerHour', Number(event.target.value))}
          />
        </SettingRow>
      </Section>

      <Section title={t('simulation.counters')}>
        {/* The counters carry their number inside the sentence — "4 offers declined" — so
            they are lines rather than tiles: a tile wants a label and a figure, and splitting
            these would be writing new words for a development screen. */}
        <ul className="grid grid-cols-2 gap-x-6 gap-y-1 list-none px-5 py-3.5 m-0">
          <li>{t('simulation.ordersCreated', { count: state.counters.ordersCreated })}</li>
          <li>{t('simulation.ridesCompleted', { count: state.counters.ridesCompleted })}</li>
          <li>{t('simulation.ridesNoShow', { count: state.counters.ridesNoShow })}</li>
          <li>{t('simulation.offersAccepted', { count: state.counters.offersAccepted })}</li>
          <li>{t('simulation.offersDeclined', { count: state.counters.offersDeclined })}</li>
          <li>{t('simulation.callsRung', { count: state.counters.callsRung })}</li>
          <li>{t('simulation.callsMissed', { count: state.counters.callsMissed })}</li>
        </ul>
      </Section>

      <Section title={t('simulation.fleet')}>
        <Table>
          <thead>
            <Tr>
              <Th>{t('simulation.column.callSign')}</Th>
              <Th>{t('simulation.column.driver')}</Th>
              <Th>{t('simulation.column.plate')}</Th>
              <Th>{t('simulation.column.phase')}</Th>
              <Th>{t('simulation.column.order')}</Th>
            </Tr>
          </thead>
          <tbody>
            {onShift.map((bot) => (
              <Tr key={bot.driverId}>
                <Td>{bot.callSign}</Td>
                <Td>
                  {bot.name}
                  {bot.outsideCity && <Sub className="ml-1.5">{t('simulation.away')}</Sub>}
                </Td>
                <Td>{bot.plate}</Td>
                <Td>{t(`simulation.phase.${bot.phase}`)}</Td>
                <Td>{bot.orderReference ?? '—'}</Td>
              </Tr>
            ))}
          </tbody>
        </Table>
      </Section>

      <SaveBar
        changes={changes}
        busy={busy}
        discardLabel={t('settings.discard')}
        saveLabel={busy ? t('settings.saving') : t('settings.save')}
        onDiscard={() => {
          editing.current = false;
          setDraft(state.settings);
        }}
        onSave={() => void save()}
      >
        {t('settings.unsaved', { count: changes })}
      </SaveBar>
    </>
  );
}
